import BusinessIcon from "@mui/icons-material/Business";
import { Alert, AlertTitle, Card, CardContent } from "@mui/material";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  GridColumns,
  PageHeader,
} from "common";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { PartnerCommissionItem } from "./partnerCommissionApi";
import PartnerCommissionForm from "./PartnerCommissionForm";
import usePartnerCommissionList from "./usePartnerCommissionList";

export interface PartnerCommissionListProps extends Omit<
  DataGridBaseBaseProps,
  "id"
> {
  showHeader?: boolean;
  onCommissionSave?: (commission: PartnerCommissionItem) => void;
}

export default function PartnerCommissionList({
  showHeader = true,
  onCommissionSave,
  ...props
}: PartnerCommissionListProps) {
  const {
    params,
    scope,
    editUrl,
    checkCommissionCoverage,
    coverageStatus,
    ...service
  } = usePartnerCommissionList();
  const { hasPermissions, canEditItem } = useAccess();

  let columns: GridColumns<PartnerCommissionItem> = [
    {
      hide: !!params.partner_id,
      hideable: !params.partner_id,
      field: "partner_id",
      headerName: I18n.t("PartnerCommission.attributes.partner_id"),
      flex: 1,
    },
    {
      field: "program.name",
      headerName: I18n.t("PartnerCommission.attributes.program_id"),
      flex: 1,
    },
    {
      field: "valid_from",
      headerName: I18n.t("PartnerCommission.attributes.valid_from"),
      type: "date",
      flex: 1,
    },
    {
      field: "headcount",
      headerName: I18n.t("PartnerCommission.attributes.headcount"),
      flex: 1,
    },
    {
      field: "commission_rate",
      headerName: I18n.t("PartnerCommission.attributes.commission_rate"),
      flex: 1,
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader
          avatar={<BusinessIcon />}
          title={I18n.t("PartnerCommission.title")}
        />
      )}
      {params?.partner_id && coverageStatus && !coverageStatus.is_complete && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Alert severity="warning">
              <AlertTitle>Hiányos jutalék beállítások</AlertTitle>
              {coverageStatus.message}
            </Alert>
          </CardContent>
        </Card>
      )}
      <DataGrid
        {...service}
        columns={columns}
        id="partner-commissions"
        afterDelete={() => {
          checkCommissionCoverage();
        }}
        renderModalForm={props => (
          <PartnerCommissionForm
            {...props}
            afterSave={(newItem, isCreate) => {
              checkCommissionCoverage();
              onCommissionSave?.(newItem);
              props.afterSave?.(newItem, isCreate);
            }}
          />
        )}
        modalProps={{ maxWidth: "sm" }}
        {...props}
        defaultState={{
          sorting: [{ field: "valid_from", direction: "desc" }],
        }}
        canCreate={hasPermissions("partner.create")}
        canDelete={hasPermissions(["partner.editany", "partner.editown"])}
        canEdit={hasPermissions(["partner.editany", "partner.editown"])}
        canDeleteRow={params => canEditItem("partner", params.row)}
        canEditRow={params => canEditItem("partner", params.row)}
        canBatchActions={hasPermissions(["partner.editany", "partner.editown"])}
        canHandleTrash={hasPermissions(["partner.editany", "partner.editown"])}
      />
    </>
  );
}
